import { Permission, Role } from "src/models";

/** Permission subjects as seeded by account-service (`<action>:<service>:<resource>`). */
export const permissions = {
  READ_SESSIONS: { action: "read", subject: "grading-service:sessions" },
  READ_OWN_SESSIONS: {
    action: "read",
    subject: "grading-service:sessions:own",
  },
  CREATE_SESSIONS: { action: "create", subject: "grading-service:sessions" },
  UPDATE_SESSIONS: { action: "update", subject: "grading-service:sessions" },
  READ_OWN_CLASSES: { action: "read", subject: "account-service:classes:own" },
  READ_CLASSES: { action: "read", subject: "account-service:classes" },
  READ_CLASS_STUDENTS: {
    action: "read",
    subject: "account-service:students:class",
  },
  UPDATE_CLASS_STUDENTS: {
    action: "update",
    subject: "account-service:students:class",
  },
  READ_SCHOOL_STUDENTS: {
    action: "read",
    subject: "account-service:students:school",
  },
  UPDATE_SCHOOL_STUDENTS: {
    action: "update",
    subject: "account-service:students:school",
  },
  READ_ACCOMMODATIONS: {
    action: "read",
    subject: "account-service:accommodations",
  },
  UPDATE_ACCOMMODATIONS: {
    action: "update",
    subject: "account-service:accommodations",
  },
  MANAGE_ROSTERING: { action: "manage", subject: "account-service:rostering" },
  /** Read-only rostering: what a suspended district admin keeps in place of manage (R19). */
  READ_ROSTERING: { action: "read", subject: "account-service:rostering" },
  CREATE_DISTRICTS: { action: "create", subject: "account-service:districts" },
  READ_DISTRICTS: { action: "read", subject: "account-service:districts" },
  READ_OWN_DISTRICT: {
    action: "read",
    subject: "account-service:districts:own",
  },
  UPDATE_OWN_DISTRICT: {
    action: "update",
    subject: "account-service:districts:own",
  },
  CREATE_SCHOOLS: { action: "create", subject: "account-service:schools" },
  READ_SCHOOLS: { action: "read", subject: "account-service:schools" },
  READ_OWN_SCHOOL: { action: "read", subject: "account-service:schools:own" },
  UPDATE_OWN_SCHOOL: {
    action: "update",
    subject: "account-service:schools:own",
  },
  CREATE_CLASSES: { action: "create", subject: "account-service:classes" },
  UPDATE_CLASSES: { action: "update", subject: "account-service:classes" },
  ADD_CLASS_STUDENTS: {
    action: "create",
    subject: "account-service:students:class",
  },
  READ_AUDIT: { action: "read", subject: "account-service:audit" },
  MANAGE_PRIVACY: {
    action: "manage",
    subject: "account-service:privacy-settings",
  },
  MANAGE_DATA_REQUESTS: {
    action: "manage",
    subject: "account-service:data-requests",
  },
  INVITE_STAFF: { action: "create", subject: "account-service:invitations" },
  READ_INVITATIONS: { action: "read", subject: "account-service:invitations" },
  UPDATE_INVITATIONS: {
    action: "update",
    subject: "account-service:invitations",
  },
  READ_STAFF: { action: "read", subject: "account-service:staff" },
  UPDATE_STAFF: { action: "update", subject: "account-service:staff" },
  READ_CLASS_REPORTS: {
    action: "read",
    subject: "reporting-service:reports:class",
  },
  READ_SCHOOL_REPORTS: {
    action: "read",
    subject: "reporting-service:reports:school",
  },
  READ_DISTRICT_REPORTS: {
    action: "read",
    subject: "reporting-service:reports:district",
  },
  READ_DOCUMENTS: {
    action: "read",
    subject: "document-worker-service:documents",
  },
} satisfies Record<string, Permission>;

export type PermissionKey = keyof typeof permissions;

/** Either opens the Rostering tab; only manage changes anything on it. */
export const ROSTERING_ACCESS: readonly Permission[] = [
  permissions.READ_ROSTERING,
  permissions.MANAGE_ROSTERING,
];

export const checkRoles = (
  allowed: readonly string[],
  roles: readonly string[] = [],
) => roles.some((r) => allowed.includes(r));

/** Roles that may use the console. Students and volunteer scorers use the app or a join code. */
export const allowedRoles: Role[] = [
  "platform_admin",
  "district_admin",
  "school_admin",
  "teacher",
];
